/**
 * The latest canonical snapshot per session, for every screen that shows one.
 *
 * One store, fed from every place a snapshot can arrive — the session stream,
 * a session read (first load, polling fallback, reconnect, foreground,
 * notification open) — so the same version rule protects all of them: a
 * snapshot is applied only when it is newer than the one held.
 *
 * Module-level like `telemetry/journey.js`: the session stream lives in
 * SessionContext, the history pages are outside it, and both must agree.
 */
import { DECISION, decide } from './canonical';

const snapshots = new Map(); // sessionUid → { snapshot, receivedAt }
const listeners = new Set();
let effectListeners = new Set();

const emit = () => { for (const l of listeners) l(); };

export function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Notified once per APPLIED snapshot: (next, previous). Never for a duplicate. */
export function onApplied(listener) {
  effectListeners.add(listener);
  return () => effectListeners.delete(listener);
}

export function getEntry(sessionUid) {
  return sessionUid ? snapshots.get(String(sessionUid).toLowerCase()) ?? null : null;
}

export function getSnapshot(sessionUid) {
  return getEntry(sessionUid)?.snapshot ?? null;
}

/**
 * Offer a snapshot. Returns the decision (see canonical.js).
 * A duplicate still counts as hearing from the session (freshness).
 */
export function applySnapshot(incoming, { now = Date.now() } = {}) {
  const key = typeof incoming?.sessionUid === 'string' ? incoming.sessionUid.toLowerCase() : null;
  const current = key ? snapshots.get(key) ?? null : null;
  const decision = decide(current?.snapshot ?? null, incoming);
  if (decision === DECISION.APPLY) {
    snapshots.set(key, { snapshot: incoming, receivedAt: now });
    emit();
    for (const l of effectListeners) {
      try { l(incoming, current?.snapshot ?? null); } catch { /* an effect never blocks an update */ }
    }
  } else if (decision === DECISION.DUPLICATE) {
    snapshots.set(key, { snapshot: current.snapshot, receivedAt: now });
    emit();
  }
  return decision;
}

/** Take the snapshot from an API session object, when the API sent one. */
export function applyFromSession(session, opts) {
  const snapshot = session?.canonical;
  return snapshot ? applySnapshot(snapshot, opts) : null;
}

export function clearSnapshot(sessionUid) {
  if (snapshots.delete(String(sessionUid ?? '').toLowerCase())) emit();
}

/** Tests only. */
export function _reset() {
  snapshots.clear();
  listeners.clear();
  effectListeners = new Set();
}
